import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { refreshToken } from '../../../../Redux/Auth/authActions';
import { partnerPortalApi } from '../Services/partnerPortalApi';
import { resolveAccessToken, usePartnerAccessToken } from './usePartnerAccessToken';

const isUnauthorizedError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return message.includes('unauthorized') || message.includes('authentication');
};

const shouldFallbackToPartnerDetail = (error) => {
  const status = error?.status;
  return status === 404 || status === 403 || status === 405;
};

export const usePSMProjectDetails = (projectId) => {
  const dispatch = useDispatch();
  const accessToken = usePartnerAccessToken();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProject = useCallback(async () => {
    if (!projectId) {
      setProject(null);
      setLoading(false);
      return;
    }

    const token = resolveAccessToken(accessToken);
    if (!token) {
      setProject(null);
      setLoading(false);
      setError('Your session has expired. Please sign in again.');
      return;
    }

    setLoading(true);
    setError(null);

    const fetchWithToken = async (activeToken) => {
      try {
        const paeProject = await partnerPortalApi.fetchPAEProjectById(activeToken, projectId);
        setProject(paeProject);
        return;
      } catch (paeError) {
        if (!shouldFallbackToPartnerDetail(paeError)) {
          throw paeError;
        }
      }

      const partnerProject = await partnerPortalApi.fetchProjectById(activeToken, projectId);
      setProject(partnerProject);
    };

    try {
      await fetchWithToken(token);
    } catch (err) {
      if (isUnauthorizedError(err) && Cookies.get('refreshToken')) {
        try {
          await dispatch(refreshToken());
          const refreshedToken = Cookies.get('accessToken');
          if (refreshedToken) {
            await fetchWithToken(refreshedToken);
            return;
          }
        } catch (refreshError) {
          setError(refreshError.message || err.message);
          setProject(null);
          return;
        }
      }

      setError(err.message);
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, dispatch, projectId]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  return {
    project,
    loading,
    error,
    refreshProject: loadProject,
    setProject,
  };
};
